// slices/providerGallerySlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import ServiceProviderGalleryService from "../services/ServiceProviderGalleryService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== HELPER: Update user data in localStorage ====================
const updateUserDataInStorage = (updates) => {
  try {
    const userDataStr = localStorage.getItem('user_data');
    if (!userDataStr) return null;
    
    const userData = JSON.parse(userDataStr);
    const updatedUserData = {
      ...userData,
      providerProfile: {
        ...(userData.providerProfile || {}),
        ...updates
      }
    };
    
    localStorage.setItem('user_data', JSON.stringify(updatedUserData));
    console.log('✅ User data updated in localStorage:', updates);
    return updatedUserData;
  } catch (error) {
    console.error('❌ Failed to update user data in localStorage:', error);
    return null;
  }
};

// ==================== ASYNC THUNKS ====================

// -------- GALLERY IMAGES --------

/**
 * Get all gallery images for a provider
 * GET /api/service-providers/:providerId/gallery
 */
export const getGalleryImages = createAsyncThunk(
  'providerGallery/getGalleryImages',
  async ({ providerId, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderGalleryService.getGalleryImages(providerId, {
        page,
        limit
      });
      
      const responseData = response?.data || response || {};
      const items = responseData?.items || [];
      const pagination = responseData?.pagination || null;
      
      console.log('[SLICE] 📊 Extracted items:', items.length);
      console.log('[SLICE] 📊 Extracted pagination:', pagination);
      
      return {
        success: true,
        data: Array.isArray(items) ? items : [],
        pagination: pagination || {
          total: Array.isArray(items) ? items.length : 0,
          page: page,
          limit: limit,
          pages: Math.ceil((Array.isArray(items) ? items.length : 0) / limit)
        }
      };
    } catch (error) {
      console.error('[SLICE] ❌ getGalleryImages error:', error);
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get a single gallery image by ID
 * GET /api/service-providers/:providerId/gallery/:imageId
 */
export const getGalleryImageById = createAsyncThunk(
  'providerGallery/getGalleryImageById',
  async ({ providerId, imageId }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderGalleryService.getGalleryImageById(providerId, imageId);
      return {
        success: true,
        data: response?.data || response
      };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const addGalleryImages = createAsyncThunk(
  'providerGallery/addGalleryImages',
  async (
    { providerId, files, options = {} },
    { rejectWithValue }
  ) => {
    try {
      const response =
        await ServiceProviderGalleryService.addGalleryImages(
          providerId,
          files,
          options
        );

      console.log(
        '[SLICE] 📥 addGalleryImages raw response:',
        JSON.stringify(response, null, 2)
      );

      /*
       * Do not assume that response.data itself is the
       * gallery array.
       *
       * Support common backend structures:
       *
       * { data: [...] }
       * { data: { items: [...] } }
       * { items: [...] }
       * [...]
       */

      let uploadedImages = [];

      if (Array.isArray(response)) {
        uploadedImages = response;
      } else if (Array.isArray(response?.data)) {
        uploadedImages = response.data;
      } else if (Array.isArray(response?.data?.items)) {
        uploadedImages = response.data.items;
      } else if (Array.isArray(response?.items)) {
        uploadedImages = response.items;
      }

      console.log(
        '[SLICE] 📊 Uploaded images returned:',
        uploadedImages.length
      );

      return {
        success: true,
        data: uploadedImages,
        message:
          response?.message ||
          response?.data?.message ||
          'Images uploaded successfully'
      };

    } catch (error) {
      console.error(
        '[SLICE] ❌ addGalleryImages error:',
        error
      );

      return rejectWithValue(
        handleError(error)
      );
    }
  }
);
/**
 * Update gallery image details
 * PUT /api/service-providers/:providerId/gallery/:imageId
 */
export const updateGalleryImage = createAsyncThunk(
  'providerGallery/updateGalleryImage',
  async ({ providerId, imageId, updateData }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderGalleryService.updateGalleryImage(
        providerId,
        imageId,
        updateData
      );
      return {
        success: true,
        data: response?.data || response
      };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Remove gallery image
 * DELETE /api/service-providers/:providerId/gallery/:imageId
 */
export const removeGalleryImage = createAsyncThunk(
  'providerGallery/removeGalleryImage',
  async ({ providerId, imageId }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderGalleryService.removeGalleryImage(providerId, imageId);
      return { ...response, imageId };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Remove multiple gallery images
 * DELETE /api/service-providers/:providerId/gallery
 */
export const removeGalleryImages = createAsyncThunk(
  'providerGallery/removeGalleryImages',
  async ({ providerId, imageIds }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderGalleryService.removeGalleryImages(providerId, imageIds);
      return { ...response, imageIds };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Reorder gallery images
 * PUT /api/service-providers/:providerId/gallery/reorder
 */
export const reorderGallery = createAsyncThunk(
  'providerGallery/reorderGallery',
  async ({ providerId, imageOrder }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderGalleryService.reorderGallery(providerId, imageOrder);
      return {
        success: true,
        data: response?.data || response
      };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Delete all gallery images for a provider
 * DELETE /api/service-providers/:providerId/gallery/all
 */
export const deleteAllGalleryImages = createAsyncThunk(
  'providerGallery/deleteAllGalleryImages',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderGalleryService.deleteAllGalleryImages(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Gallery images
  galleryImages: [],
  currentGalleryImage: null,
  
  // Pagination
  galleryPagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  },
  
  // Loading states
  loading: false,
  uploading: false,
  updating: false,
  deleting: false,
  
  // Error states
  error: null,
  uploadError: null,
  updateError: null,
  deleteError: null,
  
  // Success states
  uploadSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
  
  // UI states
  isRefreshing: false,
  hasMoreGallery: true,
  selectedImageId: null
};

const providerGallerySlice = createSlice({
  name: 'providerGallery',
  initialState,
  reducers: {
    resetGalleryState: (state) => {
      state.galleryImages = [];
      state.currentGalleryImage = null;
      state.error = null;
      state.uploadError = null;
      state.updateError = null;
      state.deleteError = null;
      state.uploadSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
      state.loading = false;
      state.uploading = false;
      state.updating = false;
      state.deleting = false;
      state.isRefreshing = false;
    },
    
    resetUploadState: (state) => {
      state.uploadSuccess = false;
      state.uploadError = null;
      state.uploading = false;
    },
    
    resetUpdateState: (state) => {
      state.updateSuccess = false;
      state.updateError = null;
      state.updating = false;
    },
    
    resetDeleteState: (state) => {
      state.deleteSuccess = false;
      state.deleteError = null;
      state.deleting = false;
    },
    
    clearGalleryErrors: (state) => {
      state.error = null;
      state.uploadError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    
    setGalleryPagination: (state, action) => {
      state.galleryPagination = { ...state.galleryPagination, ...action.payload };
    },
    
    setGalleryRefreshing: (state, action) => {
      state.isRefreshing = action.payload;
    },
    
    selectGalleryImage: (state, action) => {
      state.selectedImageId = action.payload;
    },
    
    clearCurrentGalleryImage: (state) => {
      state.currentGalleryImage = null;
    },
    
    updateGalleryImageInList: (state, action) => {
      const updatedImage = action.payload;
      const index = state.galleryImages.findIndex(img => img._id === updatedImage._id);
      if (index !== -1) {
        state.galleryImages[index] = updatedImage;
      }
    },
    
    removeGalleryImageFromList: (state, action) => {
      const imageId = action.payload;
      state.galleryImages = state.galleryImages.filter(img => img._id !== imageId);
      
      // ✅ Update localStorage
      try {
        const userDataStr = localStorage.getItem('user_data');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          const existingGallery = userData.providerProfile?.galleryImages || [];
          const updatedGallery = existingGallery.filter(img => img._id !== imageId);
          updateUserDataInStorage({ galleryImages: updatedGallery });
        }
      } catch (error) {
        console.error('❌ Failed to remove gallery image from localStorage:', error);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== GET GALLERY IMAGES ==========
      .addCase(getGalleryImages.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isRefreshing = true;
        console.log('[SLICE] 🔄 getGalleryImages pending');
      })
      .addCase(getGalleryImages.fulfilled, (state, action) => {
        state.loading = false;
        state.isRefreshing = false;
        
        console.log('[SLICE] ✅ getGalleryImages fulfilled');
        console.log('[SLICE] 📊 Payload data:', action.payload?.data?.length || 0, 'items');
        
        const data = action.payload?.data || [];
        const pagination = action.payload?.pagination || state.galleryPagination;
        
        state.galleryImages = Array.isArray(data) ? data : [];
        state.galleryPagination = {
          total: pagination?.total || state.galleryImages.length,
          page: pagination?.page || 1,
          limit: pagination?.limit || 20,
          totalPages: pagination?.pages || pagination?.totalPages || Math.ceil(state.galleryImages.length / 20)
        };
        state.hasMoreGallery = state.galleryPagination.page < state.galleryPagination.totalPages;
        
        console.log('[SLICE] 📊 Gallery images count:', state.galleryImages.length);
        console.log('[SLICE] 📊 Pagination:', state.galleryPagination);
      })
      .addCase(getGalleryImages.rejected, (state, action) => {
        state.loading = false;
        state.isRefreshing = false;
        state.error = action.payload || 'Failed to fetch gallery images';
        console.log('[SLICE] ❌ getGalleryImages rejected:', state.error);
      })
      
      // ========== GET GALLERY IMAGE BY ID ==========
      .addCase(getGalleryImageById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGalleryImageById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentGalleryImage = action.payload?.data || null;
      })
      .addCase(getGalleryImageById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch gallery image';
      })
      
      // ========== ADD GALLERY IMAGES ==========
      .addCase(addGalleryImages.pending, (state) => {
        state.uploading = true;
        state.uploadError = null;
        state.uploadSuccess = false;
        console.log('[SLICE] 🔄 addGalleryImages pending');
      })
     .addCase(addGalleryImages.fulfilled, (state, action) => {
  state.uploading = false;
  state.uploadSuccess = true;
  state.uploadError = null;

  console.log('[SLICE] ✅ addGalleryImages fulfilled');
  console.log(
    '[SLICE] Upload response:',
    action.payload
  );

  // IMPORTANT:
  // Do NOT modify galleryImages here.
  //
  // The upload endpoint confirms the upload, but the GET
  // gallery endpoint is the source of truth.
  //
  // ProviderGalleryPage will call getGalleryImages()
  // after this thunk completes.

  const uploadedImages = action.payload?.data;

  console.log(
    '[SLICE] Uploaded images returned:',
    Array.isArray(uploadedImages)
      ? uploadedImages.length
      : 'response is not an array'
  );
})
      
      // ========== UPDATE GALLERY IMAGE ==========
      .addCase(updateGalleryImage.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateGalleryImage.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const updatedImage = action.payload?.data;
        if (updatedImage && updatedImage._id) {
          const index = state.galleryImages.findIndex(img => img._id === updatedImage._id);
          if (index !== -1) {
            state.galleryImages[index] = updatedImage;
          }
          state.currentGalleryImage = updatedImage;
        }
      })
      .addCase(updateGalleryImage.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update gallery image';
        state.updateSuccess = false;
      })
      
      // ========== REMOVE GALLERY IMAGE ==========
      .addCase(removeGalleryImage.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(removeGalleryImage.fulfilled, (state, action) => {
        state.deleting = false;
        state.deleteSuccess = true;
        const imageId = action.payload?.imageId;
        if (imageId) {
          state.galleryImages = state.galleryImages.filter(img => img._id !== imageId);
          if (state.currentGalleryImage && state.currentGalleryImage._id === imageId) {
            state.currentGalleryImage = null;
          }
          
          // ✅ UPDATE LOCAL STORAGE - REMOVE GALLERY IMAGE
          try {
            const userDataStr = localStorage.getItem('user_data');
            if (userDataStr) {
              const userData = JSON.parse(userDataStr);
              const existingGallery = userData.providerProfile?.galleryImages || [];
              const updatedGallery = existingGallery.filter(img => img._id !== imageId);
              updateUserDataInStorage({ galleryImages: updatedGallery });
            }
          } catch (error) {
            console.error('❌ Failed to remove gallery image from localStorage:', error);
          }
        }
      })
      .addCase(removeGalleryImage.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload || 'Failed to remove gallery image';
        state.deleteSuccess = false;
      })
      
      // ========== REMOVE GALLERY IMAGES ==========
      .addCase(removeGalleryImages.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(removeGalleryImages.fulfilled, (state, action) => {
        state.deleting = false;
        state.deleteSuccess = true;
        const imageIds = action.payload?.imageIds || [];
        if (imageIds.length > 0) {
          state.galleryImages = state.galleryImages.filter(
            img => !imageIds.includes(img._id)
          );
          if (state.currentGalleryImage && imageIds.includes(state.currentGalleryImage._id)) {
            state.currentGalleryImage = null;
          }
          
          // ✅ UPDATE LOCAL STORAGE - REMOVE MULTIPLE GALLERY IMAGES
          try {
            const userDataStr = localStorage.getItem('user_data');
            if (userDataStr) {
              const userData = JSON.parse(userDataStr);
              const existingGallery = userData.providerProfile?.galleryImages || [];
              const updatedGallery = existingGallery.filter(img => !imageIds.includes(img._id));
              updateUserDataInStorage({ galleryImages: updatedGallery });
            }
          } catch (error) {
            console.error('❌ Failed to remove gallery images from localStorage:', error);
          }
        }
      })
      .addCase(removeGalleryImages.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload || 'Failed to remove gallery images';
        state.deleteSuccess = false;
      })
      
      // ========== REORDER GALLERY ==========
      .addCase(reorderGallery.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(reorderGallery.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const reorderedData = action.payload?.data;
        if (Array.isArray(reorderedData)) {
          state.galleryImages = reorderedData;
          
          // ✅ UPDATE LOCAL STORAGE WITH REORDERED GALLERY
          updateUserDataInStorage({ galleryImages: reorderedData });
        }
      })
      .addCase(reorderGallery.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to reorder gallery';
        state.updateSuccess = false;
      })
      
      // ========== DELETE ALL GALLERY IMAGES ==========
      .addCase(deleteAllGalleryImages.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteAllGalleryImages.fulfilled, (state) => {
        state.deleting = false;
        state.deleteSuccess = true;
        state.galleryImages = [];
        state.currentGalleryImage = null;
        
        // ✅ UPDATE LOCAL STORAGE - CLEAR GALLERY
        updateUserDataInStorage({ galleryImages: [] });
      })
      .addCase(deleteAllGalleryImages.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload || 'Failed to delete all gallery images';
        state.deleteSuccess = false;
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectGalleryState = (state) => state.providerGallery;

// Gallery selectors
export const selectGalleryImages = createSelector(
  selectGalleryState,
  (gallery) => {
    console.log('[SLICE] 🔍 selectGalleryImages returning:', gallery.galleryImages?.length || 0, 'images');
    return gallery.galleryImages || [];
  }
);

export const selectCurrentGalleryImage = createSelector(
  selectGalleryState,
  (gallery) => gallery.currentGalleryImage
);

export const selectGalleryPagination = createSelector(
  selectGalleryState,
  (gallery) => gallery.galleryPagination
);

export const selectHasMoreGallery = createSelector(
  selectGalleryState,
  (gallery) => gallery.hasMoreGallery
);

// Loading selectors
export const selectGalleryLoading = createSelector(
  selectGalleryState,
  (gallery) => gallery.loading
);

export const selectGalleryUploading = createSelector(
  selectGalleryState,
  (gallery) => gallery.uploading
);

export const selectGalleryUpdating = createSelector(
  selectGalleryState,
  (gallery) => gallery.updating
);

export const selectGalleryDeleting = createSelector(
  selectGalleryState,
  (gallery) => gallery.deleting
);

// Error selectors
export const selectGalleryError = createSelector(
  selectGalleryState,
  (gallery) => gallery.error
);

export const selectGalleryUploadError = createSelector(
  selectGalleryState,
  (gallery) => gallery.uploadError
);

export const selectGalleryUpdateError = createSelector(
  selectGalleryState,
  (gallery) => gallery.updateError
);

export const selectGalleryDeleteError = createSelector(
  selectGalleryState,
  (gallery) => gallery.deleteError
);

// Success selectors
export const selectGalleryUploadSuccess = createSelector(
  selectGalleryState,
  (gallery) => gallery.uploadSuccess
);

export const selectGalleryUpdateSuccess = createSelector(
  selectGalleryState,
  (gallery) => gallery.updateSuccess
);

export const selectGalleryDeleteSuccess = createSelector(
  selectGalleryState,
  (gallery) => gallery.deleteSuccess
);

// UI selectors
export const selectSelectedImageId = createSelector(
  selectGalleryState,
  (gallery) => gallery.selectedImageId
);

// Gallery image by ID selector
export const selectGalleryImageById = (imageId) => createSelector(
  selectGalleryImages,
  (images) => images.find(img => img._id === imageId)
);

// ==================== EXPORTS ====================

export const {
  resetGalleryState,
  resetUploadState,
  resetUpdateState,
  resetDeleteState,
  clearGalleryErrors,
  setGalleryPagination,
  setGalleryRefreshing,
  selectGalleryImage,
  clearCurrentGalleryImage,
  updateGalleryImageInList,
  removeGalleryImageFromList
} = providerGallerySlice.actions;

export default providerGallerySlice.reducer;